/**
 * 智慧无限 DK87/CX87 常规灯效：与原厂驱动 UI 顺序一致（协议 func_tab index 1，mode 0~18）
 * @see 智慧无限软件驱动协议 V1.4（机械键盘外发 xlsm）
 */

export const ZHWX_LIGHT_MODE_OFF = 18;
export const ZHWX_LIGHT_MODE_CUSTOM = 17;
export const ZHWX_LIGHT_MODE_GRADIENT = 16;

/** 渐变灯效预设（func_tab index 10 trigger_colour，0~3） */
export type ZhwxGradientPreset = {
  id: number;
  /** CSS 背景，用于 UI 圆形色块 */
  background: string;
};

export const ZHWX_GRADIENT_PRESETS: readonly ZhwxGradientPreset[] = [
  { id: 0, background: "linear-gradient(135deg, #ff5aa5, #ff0066)" },
  { id: 1, background: "linear-gradient(135deg, #9acd32, #ffd700)" },
  { id: 2, background: "linear-gradient(135deg, #0066ff, #00e5ff)" },
  { id: 3, background: "linear-gradient(135deg, #ff8800, #ffdd00)" },
];

/** 亮度/速度档位：公版驱动为 0~4（共 5 档） */
export const ZHWX_LIGHTING_LEVEL_MIN = 0;
export const ZHWX_LIGHTING_LEVEL_MAX = 4;

export const ZHWX_DEFAULT_BRIGHTNESS_MAX = ZHWX_LIGHTING_LEVEL_MAX;
export const ZHWX_DEFAULT_SPEED_MAX = ZHWX_LIGHTING_LEVEL_MAX;

/** 将 info_tab 上报的档位上限规范为 0~4 */
export function normalizeZhwxLightingLevelMax(value?: number): number {
  if (!Number.isFinite(value)) return ZHWX_LIGHTING_LEVEL_MAX;
  const rounded = Math.round(Number(value));
  if (rounded > ZHWX_LIGHTING_LEVEL_MAX) return ZHWX_LIGHTING_LEVEL_MAX;
  return Math.max(ZHWX_LIGHTING_LEVEL_MIN, rounded);
}

/** 固件速度 0=最快；UI 与亮度一致 0=最慢、max=最快 */
export function zhwxDeviceSpeedToUi(
  deviceSpeed: number,
  maxLevel: number = ZHWX_LIGHTING_LEVEL_MAX
): number {
  const max = normalizeZhwxLightingLevelMax(maxLevel);
  const clamped = clampZhwxLightingLevel(deviceSpeed, max);
  return max - clamped;
}

/** UI 速度档位转固件 func_tab 值 */
export function zhwxUiSpeedToDevice(
  uiSpeed: number,
  maxLevel: number = ZHWX_LIGHTING_LEVEL_MAX
): number {
  const max = normalizeZhwxLightingLevelMax(maxLevel);
  const clamped = clampZhwxLightingLevel(uiSpeed, max);
  return max - clamped;
}

/** 单灯效可调项（按原厂驱动 UI 行为归纳，协议未逐灯效下发能力位） */
export type ZhwxLightEffectCaps = {
  brightness: boolean;
  speed: boolean;
  direction: boolean;
  palette: boolean;
};

export type ZhwxUiLightEffect = {
  modeId: number;
  /** i18n key，如 keyboard.lighting.zhwx.wave */
  i18nKey: string;
  caps: ZhwxLightEffectCaps;
};

/** 原厂灯光页常规灯效（不含关闭；关闭由顶部 Switch 控制） */
export const ZHWX_UI_LIGHT_EFFECTS: readonly ZhwxUiLightEffect[] = [
  {
    modeId: 0,
    i18nKey: "keyboard.lighting.zhwx.wave",
    caps: { brightness: true, speed: true, direction: true, palette: true },
  },
  {
    modeId: 1,
    i18nKey: "keyboard.lighting.zhwx.colorfulCloud",
    caps: { brightness: true, speed: true, direction: true, palette: false },
  },
  {
    modeId: 2,
    i18nKey: "keyboard.lighting.zhwx.neon",
    caps: { brightness: true, speed: true, direction: true, palette: false },
  },
  {
    modeId: 3,
    i18nKey: "keyboard.lighting.zhwx.whirl",
    caps: { brightness: true, speed: true, direction: true, palette: true },
  },
  {
    modeId: 4,
    i18nKey: "keyboard.lighting.zhwx.blossom",
    caps: { brightness: true, speed: true, direction: false, palette: false },
  },
  {
    modeId: 5,
    i18nKey: "keyboard.lighting.zhwx.flying",
    caps: { brightness: true, speed: true, direction: false, palette: false },
  },
  {
    modeId: 6,
    i18nKey: "keyboard.lighting.zhwx.traverse",
    caps: { brightness: true, speed: true, direction: false, palette: false },
  },
  {
    modeId: 7,
    i18nKey: "keyboard.lighting.zhwx.rain",
    caps: { brightness: true, speed: true, direction: true, palette: true },
  },
  {
    modeId: 8,
    i18nKey: "keyboard.lighting.zhwx.spectrum",
    caps: { brightness: true, speed: true, direction: false, palette: false },
  },
  {
    modeId: 9,
    i18nKey: "keyboard.lighting.zhwx.breathing",
    caps: { brightness: true, speed: true, direction: false, palette: true },
  },
  {
    modeId: 10,
    i18nKey: "keyboard.lighting.zhwx.static",
    caps: { brightness: true, speed: false, direction: false, palette: true },
  },
  {
    modeId: 11,
    i18nKey: "keyboard.lighting.zhwx.ripple",
    caps: { brightness: true, speed: true, direction: false, palette: true },
  },
  {
    modeId: 12,
    i18nKey: "keyboard.lighting.zhwx.laser",
    caps: { brightness: true, speed: true, direction: false, palette: true },
  },
  {
    modeId: 13,
    i18nKey: "keyboard.lighting.zhwx.singlePoint",
    caps: { brightness: true, speed: true, direction: false, palette: true },
  },
  {
    modeId: 14,
    i18nKey: "keyboard.lighting.zhwx.spread",
    caps: { brightness: true, speed: false, direction: true, palette: false },
  },
  {
    modeId: 15,
    i18nKey: "keyboard.lighting.zhwx.scan",
    caps: { brightness: true, speed: false, direction: false, palette: true },
  },
  {
    modeId: 16,
    i18nKey: "keyboard.lighting.zhwx.gradient",
    caps: { brightness: true, speed: false, direction: false, palette: false },
  },
  {
    modeId: ZHWX_LIGHT_MODE_CUSTOM,
    i18nKey: "keyboard.lighting.zhwx.custom",
    caps: { brightness: true, speed: false, direction: false, palette: false },
  },
];

const DEFAULT_CAPS: ZhwxLightEffectCaps = {
  brightness: true,
  speed: true,
  direction: true,
  palette: true,
};

/** 按 modeId 查询灯效可调项 */
export function getZhwxLightEffectCaps(mode: number): ZhwxLightEffectCaps {
  if (isZhwxLightOffMode(mode)) {
    return {
      brightness: false,
      speed: false,
      direction: false,
      palette: false,
    };
  }
  return (
    ZHWX_UI_LIGHT_EFFECTS.find((item) => item.modeId === mode)?.caps ??
    DEFAULT_CAPS
  );
}

/** 不展示速度调节的灯效（兼容旧引用） */
export const ZHWX_MODES_WITHOUT_SPEED = new Set<number>(
  ZHWX_UI_LIGHT_EFFECTS.filter((item) => !item.caps.speed).map(
    (item) => item.modeId
  )
);

/** 不展示方向调节的灯效（兼容旧引用） */
export const ZHWX_MODES_WITHOUT_DIRECTION = new Set<number>(
  ZHWX_UI_LIGHT_EFFECTS.filter((item) => !item.caps.direction).map(
    (item) => item.modeId
  )
);

/** 不展示调色板的灯效（兼容旧引用） */
export const ZHWX_MODES_WITHOUT_PALETTE = new Set<number>(
  ZHWX_UI_LIGHT_EFFECTS.filter((item) => !item.caps.palette).map(
    (item) => item.modeId
  )
);

/** 预设色索引 0~8（与 func_tab index 9 一致） */
export const ZHWX_PRESET_COLOR_HEX: readonly string[] = [
  "#FF3D3D",
  "#FF8800",
  "#FFD42B",
  "#2EE944",
  "#00D8E8",
  "#2558FF",
  "#E83FD7",
  "#E8E8E8",
  "linear-gradient(135deg, #ff4d4f, #ffa940, #fadb14, #73d13d, #36cfc9, #597ef7, #9254de)",
];

export function clampZhwxLightingLevel(
  value: number,
  maxLevel: number,
  minLevel = ZHWX_LIGHTING_LEVEL_MIN
): number {
  const max = normalizeZhwxLightingLevelMax(maxLevel);
  const min = Math.max(ZHWX_LIGHTING_LEVEL_MIN, minLevel);
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, Math.round(value)));
}

export function zhwxLevelToUiPercent(
  level: number,
  maxLevel: number,
  minLevel = ZHWX_LIGHTING_LEVEL_MIN
): number {
  const clamped = clampZhwxLightingLevel(level, maxLevel, minLevel);
  const max = normalizeZhwxLightingLevelMax(maxLevel);
  const min = Math.max(ZHWX_LIGHTING_LEVEL_MIN, minLevel);
  if (max === min) return 0;
  return Math.round(((clamped - min) / (max - min)) * 100);
}

export function zhwxUiPercentToLevel(
  percent: number,
  maxLevel: number,
  minLevel = ZHWX_LIGHTING_LEVEL_MIN
): number {
  const max = normalizeZhwxLightingLevelMax(maxLevel);
  const min = Math.max(ZHWX_LIGHTING_LEVEL_MIN, minLevel);
  const normalized = Math.max(0, Math.min(100, Number(percent) || 0));
  if (max === min) return min;
  return clampZhwxLightingLevel(
    min + Math.round((normalized / 100) * (max - min)),
    max,
    min
  );
}

export function isZhwxCustomLightMode(mode: number): boolean {
  return mode === ZHWX_LIGHT_MODE_CUSTOM;
}

export function isZhwxGradientLightMode(mode: number): boolean {
  return mode === ZHWX_LIGHT_MODE_GRADIENT;
}

export function clampZhwxGradientPresetIndex(value: number): number {
  const max = ZHWX_GRADIENT_PRESETS.length - 1;
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(max, Math.round(value)));
}

export function isZhwxLightOffMode(mode: number): boolean {
  return mode === ZHWX_LIGHT_MODE_OFF;
}
